import {useCallback} from 'react';
import {useSelector, useDispatch} from 'react-redux';

import {commonSelectorFn} from '@stencil/hotels-common-reducer/src/selectors';
import {UPDATE_QUERY, IUpdate_Query} from '@stencil/hotels-common-reducer/src/types';
import {IUpdate_Srp_Config, UPDATE_SRP_CONFIG} from '@stencil/hotels-srp-reducer/src/types';
import {updateDestinationForHotelGoSuggest} from '@stencil/hotels-utils/src/common-utils';
import {
  resetSelectedProperty,
  updateSelectedProperty,
} from '@stencil/hotels-common-reducer/src/actions';

/**
 * Hook that returns a handler for updating Redux store when hotel is Selected from AutoSuggest.
 * This handler updates the query, SRP filter, and dispatches actions.
 * @returns An object containing the hotelPageGoSuggestHandler function.
 */
export default function updateHotelGoSuggestParams() {
  const dispatch = useDispatch();

  const {country} = useSelector(commonSelectorFn);

  const updateQueryCaller = useCallback(
    /**
     * Function that updates the query with the provided action data.
     * @param actionData - The action data to update the query with.
     * @returns The result of dispatching the UPDATE_QUERY action.
     */
    (actionData: IUpdate_Query['actionData']) => {
      return dispatch({type: UPDATE_QUERY, actionData});
    },
    [dispatch],
  );

  const updateSrpFilterCaller = useCallback(
    /**
     * Function that updates the SRP filter with the provided action data.
     * @param actionData - The action data to update the SRP filter with.
     */
    (actionData: IUpdate_Srp_Config['actionData']) => {
      dispatch({type: UPDATE_SRP_CONFIG, actionData});
    },
    [],
  );

  const updatePropertyDetails = useCallback(
    ({propertyId = '', propertyName = '', mmtId = '', resetFlag = false}) => {
      if (resetFlag) {
        dispatch(resetSelectedProperty());
      } else {
        dispatch(
          updateSelectedProperty({
            payload: {
              propertyId,
              propertyName,
              mmtId,
            },
          }),
        );
      }
    },
    [dispatch],
  );

  /**
   * Handler function for the hotel page go suggest.
   * @param changes - The changes object containing the selected item.
   */
  const hotelPageGoSuggestHandler = changes => {
    const hotelId = changes.selectedItem?._id;
    const updatedDestination = updateDestinationForHotelGoSuggest({
      destination: changes.selectedItem,
    });
    const obj = {
      ...changes.selectedItem,
      ...updatedDestination,
    };

    if (changes.selectedItem?.t === 'hotel') {
      updatePropertyDetails({
        propertyId: changes.selectedItem?._id,
        propertyName: changes.selectedItem?.n,
        mmtId: changes.selectedItem?.mmtId || '',
      });
    } else {
      dispatch(resetSelectedProperty());
      updatePropertyDetails({
        // resetFlag: true
        propertyId: '',
        propertyName: '',
        mmtId: '',
      });
    }

    updateQueryCaller({
      destination: {...obj, auto: 'true'},
      locus: changes?.selectedItem?.locus,
      country: changes?.selectedItem.xtr?.cc || country,
    });
    updateSrpFilterCaller({
      overriderankvhids: hotelId,
    });
  };

  return {
    hotelPageGoSuggestHandler,
  };
}
